/**
 * Screen 1 — PIN lock.
 *
 * Doubles as first-run setup: if no PIN exists yet it asks staff to choose one
 * (twice) rather than shipping a default that nobody ever changes.
 *
 * The keypad is on screen because the front desk PC may have a touch monitor,
 * but the number row and Enter work too — staff who type will always be faster.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { attemptPin, isPinSet, isValidPinFormat, setPin } from '../auth/pin';
import { usePinLock } from '../auth/usePinLock';

const PIN_LENGTH = 4;

type Mode = 'loading' | 'unlock' | 'create' | 'confirm';

export default function PinLock({ onUnlock }: { onUnlock: () => void }) {
  const [mode, setMode] = useState<Mode>('loading');
  const [entry, setEntry] = useState('');
  const [firstEntry, setFirstEntry] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const shakeRef = useRef<HTMLDivElement>(null);
  const { locked: pinLocked, lockText, note } = usePinLock('staff');
  const locked = mode === 'unlock' && pinLocked;

  useEffect(() => {
    isPinSet().then((set) => setMode(set ? 'unlock' : 'create'));
  }, []);

  const fail = useCallback((message: string) => {
    setError(message);
    setEntry('');
    // A visible shake reads faster than text for someone glancing at the screen.
    shakeRef.current?.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-8px)' },
        { transform: 'translateX(8px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 250 }
    );
  }, []);

  const submit = useCallback(
    async (pin: string) => {
      setBusy(true);
      setError('');
      try {
        if (mode === 'unlock') {
          const attempt = await attemptPin(pin);
          if (attempt.ok) onUnlock();
          else fail(note(attempt));
        } else if (mode === 'create') {
          setFirstEntry(pin);
          setEntry('');
          setMode('confirm');
        } else if (mode === 'confirm') {
          if (pin === firstEntry) {
            await setPin(pin);
            onUnlock();
          } else {
            setFirstEntry('');
            setMode('create');
            fail('Those did not match — start again');
          }
        }
      } catch (e) {
        fail(e instanceof Error ? e.message : 'Something went wrong');
      } finally {
        setBusy(false);
      }
    },
    [mode, firstEntry, onUnlock, fail, note]
  );

  // Auto-submit on the fourth digit — no Enter key needed for the common case.
  useEffect(() => {
    if (entry.length === PIN_LENGTH && !busy && mode !== 'loading') {
      void submit(entry);
    }
  }, [entry, busy, mode, submit]);

  const press = (digit: string) => {
    if (busy || locked) return;
    setError('');
    setEntry((e) => (e.length < PIN_LENGTH ? e + digit : e));
  };

  const backspace = () => {
    setError('');
    setEntry((e) => e.slice(0, -1));
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') backspace();
      else if (e.key === 'Escape') setEntry('');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (mode === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <img src="/logo.png" alt="" className="h-20 w-auto animate-pulse" />
      </div>
    );
  }

  // While locked the live countdown replaces whatever the last failure said.
  const shownError = locked ? lockText : error;

  const heading =
    mode === 'unlock'
      ? 'Enter your PIN'
      : mode === 'create'
        ? 'Choose a 4-digit PIN'
        : 'Enter it again to confirm';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-6 py-10">
      <img src="/logo.png" alt="SK Martial Arts Academy" className="mb-6 h-24 w-auto" />
      <h1 className="mb-1 text-center text-3xl text-brand">SK MARTIAL ARTS</h1>
      <p className="mb-10 text-xs uppercase tracking-button text-white/40">Front Desk System</p>

      <div ref={shakeRef} className="w-full max-w-xs">
        <p className="mb-6 text-center text-lg text-white/70">{heading}</p>

        {/* Dots */}
        <div className="mb-8 flex justify-center gap-4" aria-live="polite">
          {Array.from({ length: PIN_LENGTH }).map((_, i) => (
            <span
              key={i}
              className={`h-4 w-4 rounded-full border-2 transition-all duration-brand ${
                i < entry.length ? 'border-brand bg-brand' : 'border-edge'
              }`}
            />
          ))}
        </div>

        <p
          className={`mb-4 h-6 text-center text-sm font-semibold text-status-expired ${
            shownError ? 'opacity-100' : 'opacity-0'
          }`}
          role="alert"
        >
          {shownError || ' '}
        </p>

        <div className="grid grid-cols-3 gap-3">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button
              key={d}
              onClick={() => press(d)}
              disabled={busy || locked}
              className="tabular min-h-touch rounded-btn border-2 border-edge bg-surface text-2xl font-bold
                         transition-all duration-brand hover:border-brand active:bg-brand
                         disabled:opacity-40"
            >
              {d}
            </button>
          ))}
          <button
            onClick={() => setEntry('')}
            disabled={busy || locked}
            className="min-h-touch rounded-btn border-2 border-edge bg-surface text-xs font-bold uppercase
                       tracking-button text-white/50 transition-all duration-brand hover:border-brand"
          >
            Clear
          </button>
          <button
            onClick={() => press('0')}
            disabled={busy || locked}
            className="tabular min-h-touch rounded-btn border-2 border-edge bg-surface text-2xl font-bold
                       transition-all duration-brand hover:border-brand active:bg-brand disabled:opacity-40"
          >
            0
          </button>
          <button
            onClick={backspace}
            disabled={busy || locked}
            className="min-h-touch rounded-btn border-2 border-edge bg-surface text-2xl
                       transition-all duration-brand hover:border-brand"
            aria-label="Backspace"
          >
            ⌫
          </button>
        </div>

        {mode !== 'unlock' && (
          <p className="mt-6 text-center text-xs leading-relaxed text-white/30">
            The PIN keeps passers-by out of the front desk. It is not a password —
            do not reuse one that protects anything else.
          </p>
        )}
      </div>
    </div>
  );
}

export { isValidPinFormat };
